// Enhanced TutorAgent.jsx with proper session management matching backend
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api, { getCurrentUserId } from "../../services/api";
import Layout from "../../components/Layout/Layout";
import Markdown from "../../components/Markdown/Markdown";
import useLearnerSnapshot from "../../hooks/useLearnerSnapshot";
import useAgentTracker from "../../hooks/useAgentTracker";
import {
  ArrowUp, Check, Copy, GraduationCap, HelpCircle, History, Lightbulb, ListChecks, MessageSquarePlus, PanelLeftClose,
  RotateCw, Sparkles, Trash2, X,
} from "lucide-react";
import "./tutor.css";
import "./tutor-chat.css";

// Shown on the start screen when we do not yet know what the student is working on
const STARTERS = [
  { icon: Lightbulb, title: "Explain a topic", hint: "In simple steps", text: "Can you explain a topic from my class to me? Ask me which one." },
  { icon: HelpCircle, title: "Help with homework", hint: "I'll guide you, not just give answers", text: "I need help with my homework. Please guide me step by step." },
  { icon: ListChecks, title: "Quiz me", hint: "A few quick questions", text: "Ask me a few quick questions to test what I know. Ask me which subject first." },
  { icon: Sparkles, title: "Get ready for a test", hint: "What to revise and how", text: "I have a test coming up. Help me revise. Ask me what it is on." },
];

// One tap after a tutor answer
const FOLLOW_UPS = [
  { label: "Explain it more simply", text: "Can you explain that again more simply?" },
  { label: "Give me an example", text: "Can you give me one clear example of that?" },
  { label: "Quiz me on this", text: "Ask me one question to check that I understood." },
];

const timeLabel = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";
};

const TutorAgent = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  // Load sidebar state from localStorage, default to open on a computer. On a phone it covers the whole screen, so it
  // always starts closed there (a choice remembered from a desktop visit must not trap the student behind it).
  const [showSidebar, setShowSidebar] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 768) return false;
    const saved = localStorage.getItem("tutorSidebarOpen");
    return saved !== null ? saved === "true" : true;
  });
  const [conversationSummary, setConversationSummary] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const location = useLocation();
  const messagesEndRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const sessionIdRef = useRef(null);
  const initializedRef = useRef(false);

  // What the other agents know about this student (quizzes, courses, earlier questions)
  const { snapshot } = useLearnerSnapshot();

  // Agent tracking
  const { startAgentSession, recordInteraction, endAgentSession } =
    useAgentTracker();

  // Subject and topic from navigation state or defaults
  const query = new URLSearchParams(location.search);
  const subject = location.state?.context?.subject || query.get("subject") || "General";
  const topic = location.state?.context?.topic || query.get("topic") || null;

  // Get user ID consistently with other agents
  const [userId] = useState(() => {
    const userData = JSON.parse(localStorage.getItem("user") || "{}");

    // The signed-in account (from the token) comes first: it is the id the server files conversations under, and
    // it can never be a stale id left in the browser by another account (which the server refuses with a 403).
    const consistentId =
      getCurrentUserId() ||
      userData.user_id ||
      localStorage.getItem("user_id") ||
      userData.student_id ||
      localStorage.getItem("student_id");

    if (consistentId) {
      return consistentId;
    }

    // Generate new ID as last resort
    const newId = `user_${Date.now()}`;
    localStorage.setItem("user_id", newId);
    return newId;
  });

  // Scroll to bottom when new messages are added
  const scrollToBottom = useCallback(() => {
    const box = scrollRef.current;
    if (box) box.scrollTo({ top: box.scrollHeight, behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load conversation sessions for sidebar
  const loadSessions = useCallback(async () => {
    try {
      setLoadingSessions(true);
      const response = await api.get(`/api/conversations/sessions/${userId}`);
      setSessions(response.data || []);
    } catch (error) {
      console.error("Failed to load sessions:", error);
    } finally {
      setLoadingSessions(false);
    }
  }, [userId]);

  // Load conversation summary
  const loadSummary = useCallback(async () => {
    try {
      const response = await api.get(`/api/conversations/summary/${userId}`);
      setConversationSummary(response.data);
    } catch (error) {
      console.error("Failed to load summary:", error);
    }
  }, [userId]);

  // Load messages from a specific session
  const loadSession = useCallback(
    async (sessionId) => {
      try {
        setLoading(true);
        const response = await api.get(
          `/api/conversations/sessions/${userId}/${sessionId}/messages`
        );
        const sessionMessages = response.data || [];

        if (sessionMessages.length > 0) {
          const formattedMessages = sessionMessages.flatMap((msg) => [
            {
              type: "user",
              content: msg.question,
              timestamp: msg.timestamp,
            },
            {
              type: "agent",
              content: msg.answer,
              timestamp: msg.timestamp,
            },
          ]);
          setMessages(formattedMessages);
          setCurrentSessionId(sessionId);
          sessionIdRef.current = sessionId;

          // Switch to this session
          await api.post("/api/conversations/sessions/switch", {
            user_id: userId,
            session_id: sessionId,
          });
        } else {
          // Empty session, just set it as current
          setMessages([]);
          setCurrentSessionId(sessionId);
          sessionIdRef.current = sessionId;
        }
        if (window.innerWidth <= 900) setShowSidebar(false);   // a phone shows the chat, not the list
      } catch (error) {
        console.error("Failed to load session:", error);
        setError("Failed to load conversation. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  // Delete a conversation session
  const deleteSession = useCallback(
    async (sessionId, event) => {
      // Stop propagation to prevent loading the session when clicking delete
      if (event) {
        event.stopPropagation();
      }

      const confirmDelete = window.confirm(
        "Are you sure you want to delete this conversation? This cannot be undone."
      );

      if (!confirmDelete) return;

      try {
        setLoading(true);

        // Delete the session via API
        await api.delete(`/api/conversations/sessions/${userId}/${sessionId}`);

        // If the deleted session was the current one, clear messages
        if (currentSessionId === sessionId) {
          setMessages([]);
          setCurrentSessionId(null);
          sessionIdRef.current = null;

          // Show welcome message
          const welcomeMessage = {
            type: "agent",
            content: `Hi there! I'm your AI tutor. What would you like to learn about today?`,
            timestamp: new Date().toISOString(),
          };
          setMessages([welcomeMessage]);
        }

        // Reload sessions list
        await loadSessions();
      } catch (error) {
        console.error("Failed to delete session:", error);
        setError("Failed to delete conversation. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [userId, currentSessionId, loadSessions]
  );

  // Start a fresh conversation. This only creates an empty session on the server (no AI call), so it works
  // instantly and keeps working even when the AI service is slow or unavailable.
  const createNewSession = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setRetryCount(0);

      // The server also closes the previous active session
      const response = await api.post("/api/conversations/sessions/new", {
        user_id: userId,
        title: "New Conversation",
      });
      const newSessionId = response.data?.session_id;
      if (!newSessionId) throw new Error("No session id returned");

      setCurrentSessionId(newSessionId);
      sessionIdRef.current = newSessionId;
      setMessages([
        {
          type: "agent",
          content: "Hi there! I'm your AI tutor. What would you like to learn about today?",
          timestamp: new Date().toISOString(),
        },
      ]);

      // Reload sessions to show the new one
      await loadSessions();
    } catch (error) {
      console.error("Failed to create new session:", error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      setError(
        status === 401
          ? "Your session has expired. Please sign in again."
          : !error?.response
          ? "We couldn't reach the server. Check your internet connection and try again."
          : `Failed to create new conversation${detail && typeof detail === "string" ? `: ${detail}` : ". Please try again."}`
      );
    } finally {
      setLoading(false);
    }
  }, [userId, loadSessions]);

  // Tutor replies are Markdown (bold, lists, tables): render them properly instead of showing the symbols
  const formatResponse = useCallback(
    (text) => (text ? <Markdown className="tutor-md">{text}</Markdown> : text),
    []
  );

  // Initialize agent session and load data
  useEffect(() => {
    // Prevent multiple initializations in strict mode
    if (initializedRef.current) return;
    initializedRef.current = true;

    const initSession = async () => {
      try {
        // Load existing sessions
        await loadSessions();
        await loadSummary();

        // Get or create active session
        const activeSessionResponse = await api.get(
          `/api/conversations/active/${userId}`
        );
        const activeSession = activeSessionResponse.data;

        if (activeSession && activeSession.session_id) {
          const sessionId = activeSession.session_id;
          setCurrentSessionId(sessionId);
          sessionIdRef.current = sessionId;

          // Load messages from active session
          try {
            const messagesResponse = await api.get(
              `/api/conversations/sessions/${userId}/${sessionId}/messages`
            );
            const sessionMessages = messagesResponse.data || [];

            if (sessionMessages.length > 0) {
              const formattedMessages = sessionMessages.flatMap((msg) => [
                {
                  type: "user",
                  content: msg.question,
                  timestamp: msg.timestamp,
                },
                {
                  type: "agent",
                  content: msg.answer,
                  timestamp: msg.timestamp,
                },
              ]);
              setMessages(formattedMessages);
            } else {
              // Empty session - show welcome message
              const welcomeMessage = {
                type: "agent",
                content: `Hi there! I'm your AI tutor. What would you like to learn about today?`,
                timestamp: new Date().toISOString(),
              };
              setMessages([welcomeMessage]);
            }
          } catch (err) {
            console.error("Failed to load session messages:", err);
            // Show welcome message on error
            const welcomeMessage = {
              type: "agent",
              content: `Hi there! I'm your AI tutor. What would you like to learn about today?`,
              timestamp: new Date().toISOString(),
            };
            setMessages([welcomeMessage]);
          }
        } else {
          // No active session - show welcome message
          const welcomeMessage = {
            type: "agent",
            content: `Hi there! I'm your AI tutor, ready to help you learn${
              subject !== "General" ? ` about ${subject}` : ""
            }${
              topic ? `, especially ${topic}` : ""
            }. What's on your mind today?`,
            timestamp: new Date().toISOString(),
          };
          setMessages([welcomeMessage]);
        }

        // Start agent tracking session
        const trackingSessionId = await startAgentSession(
          "tutor",
          subject,
          topic
        );
        console.log("Agent tracking session started:", trackingSessionId);
      } catch (error) {
        console.error("Failed to initialize agent session:", error);
        // Still show welcome message even if initialization fails
        const welcomeMessage = {
          type: "agent",
          content:
            "Hello! I'm your AI tutor and I'm here to help you learn. What would you like to explore today?",
          timestamp: new Date().toISOString(),
        };
        setMessages([welcomeMessage]);
      }
    };

    initSession();

    // Cleanup function
    return () => {
      if (sessionIdRef.current) {
        endAgentSession(sessionIdRef.current);
        console.log("Agent session ended:", sessionIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    document.body.classList.add("tt-no-floating-toggle");
    return () => document.body.classList.remove("tt-no-floating-toggle");
  }, []);

  const copyMessage = useCallback(async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex((c) => (c === index ? null : c)), 1600);
    } catch {
      /* clipboard blocked: nothing to do */
    }
  }, []);

  // Persist sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem("tutorSidebarOpen", showSidebar.toString());
  }, [showSidebar]);

  // Enhanced message sending with proper session tracking
  const sendMessage = useCallback(async (suggested) => {
    // A suggestion chip passes its own text; the send button passes a click event
    const text = (typeof suggested === "string" ? suggested : input).trim();
    if (!text) return;

    const userMessage = {
      type: "user",
      content: text,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const currentInput = text;
    setInput("");
    setLoading(true);
    setError(null);

    const startTime = Date.now();

    try {
      // Enhanced request payload matching backend expectations
      const requestPayload = {
        message: currentInput,
        user_id: userId,
        session_id: currentSessionId, // Include current session ID
        subject: subject !== "General" ? subject : undefined,
        topic: topic,
        // Don't include conversation_history - backend will fetch from database
      };

      console.log("Sending request:", {
        message: currentInput.substring(0, 50) + "...",
        session_id: currentSessionId,
        has_session: !!currentSessionId,
      });

      const response = await api.post("/api/agents/tutor/chat", requestPayload);

      if (!response.data || !response.data.response) {
        throw new Error("Invalid response from server");
      }

      // Update session ID if it changed (e.g., new session was created)
      const newSessionId = response.data.conversation_context?.session_id;
      if (newSessionId && newSessionId !== currentSessionId) {
        console.log("Session ID updated:", newSessionId);
        setCurrentSessionId(newSessionId);
        sessionIdRef.current = newSessionId;
      }

      const tutorResponse = {
        type: "agent",
        content: response.data.response,
        mode: response.data.mode || "ai_powered",
        context: response.data.conversation_context,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, tutorResponse]);
      setRetryCount(0); // Reset retry count on success

      // Reload sessions to update sidebar
      await loadSessions();

      // Record the interaction for analytics
      const duration = Date.now() - startTime;
      if (sessionIdRef.current) {
        try {
          await recordInteraction(
            sessionIdRef.current,
            currentInput,
            response.data.response,
            duration
          );
        } catch (trackingError) {
          console.warn("Failed to record interaction:", trackingError);
        }
      }
    } catch (error) {
      console.error("Tutor chat error:", error);

      // Enhanced error handling with retry logic
      let errorMessage = "I'm having trouble connecting right now. ";
      let canRetry = false;

      if (error.response?.status === 503) {
        errorMessage += "The tutoring service is temporarily unavailable.";
        canRetry = retryCount < 2;
      } else if (error.response?.status === 400) {
        errorMessage +=
          "There was an issue with your message. Could you try rephrasing it?";
      } else if (error.response?.status === 429) {
        errorMessage +=
          "I'm getting a lot of requests right now. Please wait a moment before trying again.";
        canRetry = true;
      } else if (error.code === "NETWORK_ERROR" || !error.response) {
        errorMessage += "Please check your internet connection.";
        canRetry = retryCount < 3;
      } else {
        errorMessage += "Please try again in a moment.";
        canRetry = retryCount < 2;
      }

      const errorResponse = {
        type: "agent",
        content: errorMessage,
        isError: true,
        canRetry: canRetry,
        originalMessage: currentInput,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, errorResponse]);
      setError(errorMessage);

      if (canRetry) {
        setRetryCount((prev) => prev + 1);
      }
    } finally {
      setLoading(false);
    }
  }, [
    input,
    subject,
    topic,
    retryCount,
    recordInteraction,
    userId,
    currentSessionId,
    loadSessions,
  ]);

  // Clear conversation - creates NEW session with force_new_session flag
  const clearConversation = useCallback(async () => {
    if (messages.length > 1) {
      const confirmClear = window.confirm(
        "Are you sure you want to start a new conversation? This will create a fresh session."
      );
      if (!confirmClear) return;
    }

    // Use createNewSession which properly handles force_new_session
    await createNewSession();
  }, [messages.length, createNewSession]);

  // Retry failed message
  const retryMessage = useCallback(
    async (originalMessage) => {
      setInput(originalMessage);
      setTimeout(() => {
        sendMessage();
      }, 100);
    },
    [sendMessage]
  );

  // Handle key press
  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!loading && input.trim()) {
          sendMessage();
        }
      }
    },
    [sendMessage, loading, input]
  );

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height =
        Math.min(inputRef.current.scrollHeight, 120) + "px";
    }
  }, [input]);

  // Start screen: only the welcome message so far
  const isFresh = messages.length <= 1 && messages.every((m) => m.type === "agent" && !m.isError);
  const welcomeText =
    snapshot?.greeting ||
    (messages[0] && messages[0].content && !/^Hi there! I'm your AI tutor/.test(messages[0].content) ? messages[0].content : "") ||
    "Ask me anything about your lessons. I'll explain it step by step.";
  const personalStarters = (() => {
    if (!snapshot || snapshot.empty) return [];
    const picks = [];
    (snapshot.studying || []).slice(0, 1).forEach((c) =>
      picks.push({ icon: Lightbulb, title: `Continue: ${c.chapter}`, hint: c.subject, text: `Help me with “${c.chapter}” in ${c.subject}` }));
    (snapshot.focus || []).slice(0, 2).forEach((f) =>
      picks.push({
        icon: HelpCircle,
        title: f.kind === "missed" ? `Go over: ${f.topic}` : `Understand ${f.topic}`,
        hint: f.subject || "From your recent work",
        text: f.kind === "missed" ? `Explain this to me: ${f.topic}` : `Help me understand ${f.topic} in ${f.subject}`,
      }));
    return picks.slice(0, 3);
  })();
  const starters = [...personalStarters, ...STARTERS].slice(0, 4);
  const lastTutorIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].type === "agent" && !messages[i].isError) return i;
    return -1;
  })();
  const hasTags = subject !== "General" || topic;

  return (
    <Layout fullBleed>
      <div className="tt-page">
        {/* ---------- conversation history ---------- */}
        <aside className={`tt-history ${showSidebar ? "open" : "closed"}`} aria-label="Your conversations">
          <div className="tt-history-head">
            <h2>Your conversations</h2>
            <button type="button" className="tt-icon-btn" onClick={() => setShowSidebar(false)} aria-label="Close conversation list">
              <PanelLeftClose size={18} />
            </button>
          </div>
          <button type="button" className="tt-history-new" onClick={createNewSession} disabled={loading}>
            <MessageSquarePlus size={16} aria-hidden="true" /> New chat
          </button>
          <div className="tt-sessions">
            {loadingSessions ? (
              <div className="tt-history-empty">Loading…</div>
            ) : sessions.length === 0 ? (
              <div className="tt-history-empty">No conversations yet. Ask your first question!</div>
            ) : (
              sessions.map((session) => (
                <div
                  key={session.session_id}
                  className={`tt-session ${currentSessionId === session.session_id ? "active" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => loadSession(session.session_id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); loadSession(session.session_id); } }}
                >
                  <div className="tt-session-title">{session.title || "Conversation"}</div>
                  <div className="tt-session-meta">
                    {session.last_message ? session.last_message : "New conversation"}
                  </div>
                  <button
                    type="button"
                    className="tt-session-del"
                    onClick={(e) => deleteSession(session.session_id, e)}
                    aria-label={`Delete conversation: ${session.title || "Conversation"}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))
            )}
          </div>
          {conversationSummary && (
            <div className="tt-history-foot">
              {conversationSummary.total_sessions} chats · {conversationSummary.total_messages} messages
            </div>
          )}
        </aside>
        <div className="tt-scrim" onClick={() => setShowSidebar(false)} aria-hidden="true" />

        {/* ---------- chat ---------- */}
        <section className="tt-chat" aria-label="AI tutor chat">
          <header className="tt-bar">
            <button
              type="button"
              className="tt-icon-btn"
              onClick={() => setShowSidebar(!showSidebar)}
              aria-label={showSidebar ? "Hide conversations" : "Show conversations"}
              aria-expanded={showSidebar}
            >
              <History size={19} />
            </button>
            <div className="tt-bar-title">
              <span className="tt-avatar" aria-hidden="true"><GraduationCap size={19} /></span>
              <div style={{ minWidth: 0 }}>
                <h1>AI Tutor</h1>
                {hasTags ? (
                  <p>
                    <span className="tt-tags">
                      {subject !== "General" && <span className="tt-tag">{subject}</span>}
                      {topic && <span className="tt-tag">{topic}</span>}
                    </span>
                  </p>
                ) : (
                  <p>Ask anything about your lessons</p>
                )}
              </div>
            </div>
            <button type="button" className="tt-new" onClick={clearConversation} disabled={loading} title="Start a new conversation">
              <MessageSquarePlus size={16} aria-hidden="true" /> <span className="tt-new-label">New chat</span>
            </button>
          </header>

          <div className="tt-scroll" ref={scrollRef}>
            <div className="tt-thread" role="log" aria-live="polite" aria-label="Conversation">
              {isFresh ? (
                <div className="tt-welcome">
                  <span className="tt-avatar lg" aria-hidden="true"><GraduationCap size={32} /></span>
                  <h2>What would you like to learn today?</h2>
                  <p>{welcomeText}</p>
                  <div className="tt-starters-label">{personalStarters.length ? "Pick up where you left off" : "Try one of these"}</div>
                  <div className="tt-starters">
                    {starters.map(({ icon: Icon, title, hint, text }) => (
                      <button key={title} type="button" className="tt-starter" onClick={() => sendMessage(text)} disabled={loading}>
                        <span className="tt-starter-icon" aria-hidden="true"><Icon size={16} /></span>
                        <span>
                          {title}
                          <small>{hint}</small>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((message, index) => {
                  const isTutor = message.type === "agent";
                  return (
                    <div key={index} className={`tt-msg ${isTutor ? (message.isError ? "tutor error" : "tutor") : "user"}`}>
                      {isTutor && <span className="tt-avatar" aria-hidden="true"><GraduationCap size={18} /></span>}
                      <div className="tt-msg-col">
                        <div className="tt-bubble">
                          {isTutor ? formatResponse(message.content) : message.content}
                        </div>
                        <div className="tt-meta">
                          {timeLabel(message.timestamp) && <span className="tt-time">{timeLabel(message.timestamp)}</span>}
                          {isTutor && !message.isError && (
                            <button type="button" className="tt-chip-btn" onClick={() => copyMessage(message.content, index)}>
                              {copiedIndex === index ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
                              {copiedIndex === index ? "Copied" : "Copy"}
                            </button>
                          )}
                          {message.isError && message.canRetry && (
                            <button type="button" className="tt-chip-btn" onClick={() => retryMessage(message.originalMessage)} disabled={loading}>
                              <RotateCw size={13} aria-hidden="true" /> Try again
                            </button>
                          )}
                        </div>
                        {isTutor && index === lastTutorIndex && index > 0 && !loading && (
                          <div className="tt-followups">
                            {FOLLOW_UPS.map((f) => (
                              <button key={f.label} type="button" className="tt-chip-btn" onClick={() => sendMessage(f.text)}>
                                {f.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}

              {loading && (
                <div className="tt-msg tutor" aria-label="Your tutor is thinking">
                  <span className="tt-avatar" aria-hidden="true"><GraduationCap size={18} /></span>
                  <div className="tt-msg-col">
                    <div className="tt-bubble tt-typing"><span /><span /><span /></div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <footer className="tt-composer">
            <div className="tt-composer-inner">
              {error && (
                <div className="tt-error" role="alert">
                  <span>{error}</span>
                  <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
                    <X size={16} />
                  </button>
                </div>
              )}
              <div className="tt-box">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Ask your tutor anything…"
                  disabled={loading}
                  rows={1}
                  maxLength={2000}
                  aria-label="Your message"
                />
                <button type="button" className="tt-send" onClick={sendMessage} disabled={loading || !input.trim()} aria-label="Send message">
                  <ArrowUp size={20} />
                </button>
              </div>
              <p className="tt-hint">Enter to send · Shift + Enter for a new line</p>
            </div>
          </footer>
        </section>
      </div>
    </Layout>
  );
};

export default TutorAgent;
